// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import './styles/main.css';
import {
  ABOUT_BODY,
  DROP_HEADING,
  DROP_SUB,
  DROP_TITLE,
  FOLDER_NOTE,
  GLOSSARY,
  HERO_BODY,
  HERO_TITLE,
  HONESTY,
  HOW_STEPS,
  LIMITS,
  PRIVACY_NOT,
  PRIVACY_PROTECTED,
  PRIVACY_TRUST,
  TAGLINE,
  TRUST_BODY,
  TRUST_STRONG,
} from './copy';
import { bindLog, clearLog, logEvent, logText } from './eventlog';
import { headlineFor, ledgerCsv, reportHtml } from './artefacts';
import { defaultPriceTable, money, ratesFrom } from './domain/pricing';
import {
  createDrawer,
  createModalHost,
  el,
  formatBytes,
  formatCount,
  formatTokens,
  renderCopy,
  toast,
} from './ui/shell';
import type { Analysis, PriceTable, Progress } from './types';
import type { AnalyseRequest, WorkerOut } from './worker';

const THEME_KEY = 'runledger.theme';
const PRICES_KEY = 'runledger.prices';

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

/* ── framebusting ───────────────────────────────────────────────────────────────────────────
   The threat model says runledger refuses to render inside a frame, so the code has to do it.
   `frame-ancestors` is inert in a <meta> policy and GitHub Pages cannot set headers. */
try {
  if (window.top !== window.self) {
    document.documentElement.replaceChildren();
    document.body.textContent = 'runledger will not run inside a frame. Open it at https://runledger.benrichardson.dev';
  }
} catch {
  document.documentElement.replaceChildren();
  document.body.textContent = 'runledger will not run inside a frame. Open it at https://runledger.benrichardson.dev';
}

/* ── state ─────────────────────────────────────────────────────────────────────────────────── */

let worker: Worker | null = null;
let analysis: Analysis | null = null;
let rowLimit = 30;

function loadPrices(): PriceTable {
  const table = defaultPriceTable();
  try {
    const raw = localStorage.getItem(PRICES_KEY);
    if (!raw) return table;
    const saved = JSON.parse(raw) as Record<string, { input: number; output: number }>;
    for (const [family, v] of Object.entries(saved)) {
      const entry = table.families[family];
      if (entry && Number.isFinite(v.input) && Number.isFinite(v.output)) {
        entry.rates = ratesFrom(v.input, v.output);
      }
    }
    table.asOf = `${table.asOf} (edited)`;
  } catch {
    /* a corrupt preference must never stop the tool loading */
  }
  return table;
}

let priceTable = loadPrices();

/* ── theme ─────────────────────────────────────────────────────────────────────────────────── */

function applyTheme(t: string): void {
  document.documentElement.setAttribute('data-theme', t);
  try {
    localStorage.setItem(THEME_KEY, t);
  } catch {
    /* private mode */
  }
}
applyTheme(localStorage.getItem(THEME_KEY) ?? 'dark');
$('theme-toggle').addEventListener('click', () => {
  applyTheme(document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark');
});

/* ── static copy ───────────────────────────────────────────────────────────────────────────── */

$('brand-tagline').textContent = TAGLINE;
$('hero-title').textContent = HERO_TITLE;
$('hero-body').replaceChildren(renderCopy(HERO_BODY));
$('trust-strong').textContent = TRUST_STRONG;
$('trust-body').textContent = TRUST_BODY;
$('drop-heading').textContent = DROP_HEADING;
$('dz-title').replaceChildren(renderCopy(DROP_TITLE));
$('dz-sub').replaceChildren(renderCopy(DROP_SUB));
$('folder-note').textContent = FOLDER_NOTE;
$('honesty').textContent = HONESTY;
$('limits-list').replaceChildren(...LIMITS.map((l) => el('li', {}, [renderCopy(l)])));

/* ── overlays ──────────────────────────────────────────────────────────────────────────────── */

const modal = createModalHost($('modal-host'));
// The drawer wires itself to its toggle and its own close control; nothing else needs the handle.
createDrawer($('drawer'), $('log-toggle'), $('log-close'));
bindLog($('drawer-list') as HTMLOListElement);

$('log-copy').addEventListener('click', async () => {
  try {
    await navigator.clipboard.writeText(logText());
    toast('Event log copied.');
  } catch {
    toast('Your browser would not let the page write to the clipboard.');
  }
});

function list(items: string[]): HTMLUListElement {
  return el('ul', {}, items.map((i) => el('li', {}, [renderCopy(i)])));
}

function priceEditor(): HTMLElement {
  const wrap = el('div', { class: 'price-editor' });
  wrap.append(
    el('p', {}, [
      renderCopy(
        `Taken from the published list on <strong>${priceTable.asOf}</strong>. Enter the base input and ` +
          `output price per million tokens; the [[cache-write|cache-write]] and ` +
          `[[cache-read|cache-read]] columns are the documented multipliers of the input price ` +
          `(1.25x for the five-minute store, 2x for the one-hour store, 0.1x for a read) and are ` +
          `not editable, because they are structural rather than per-model.`,
      ),
    ]),
  );
  const table = el('table', { class: 'data' });
  const head = el('tr', {}, [
    el('th', {}, ['model']),
    el('th', { class: 'n' }, ['input $/M']),
    el('th', { class: 'n' }, ['output $/M']),
    el('th', { class: 'n' }, ['read $/M']),
  ]);
  table.append(el('thead', {}, [head]));
  const body = el('tbody');
  for (const entry of Object.values(priceTable.families)) {
    const inputEl = el('input', { type: 'number', step: '0.01', min: '0', value: String(entry.rates.input) });
    const outputEl = el('input', { type: 'number', step: '0.01', min: '0', value: String(entry.rates.output) });
    const readCell = el('td', { class: 'n mono' }, [entry.rates.read.toFixed(3)]);
    const onChange = (): void => {
      const inp = Number(inputEl.value);
      const out = Number(outputEl.value);
      if (!Number.isFinite(inp) || !Number.isFinite(out) || inp < 0 || out < 0) return;
      entry.rates = ratesFrom(inp, out);
      readCell.textContent = entry.rates.read.toFixed(3);
      persistPrices();
      if (analysis) rerun();
    };
    inputEl.addEventListener('change', onChange);
    outputEl.addEventListener('change', onChange);
    body.append(
      el('tr', {}, [
        el('td', {}, [entry.label]),
        el('td', { class: 'n' }, [inputEl]),
        el('td', { class: 'n' }, [outputEl]),
        readCell,
      ]),
    );
  }
  table.append(body);
  wrap.append(table);
  const reset = el('button', { type: 'button', class: 'btn btn-ghost' }, ['Reset to the published list']);
  reset.addEventListener('click', () => {
    try {
      localStorage.removeItem(PRICES_KEY);
    } catch {
      /* private mode */
    }
    priceTable = defaultPriceTable();
    logEvent('Price table reset to the published list.', 'info');
    modal.close();
    if (analysis) rerun();
  });
  wrap.append(el('p', {}, [reset]));
  wrap.append(
    el('p', { class: 'field-note' }, [
      'Source: ',
      el('a', { href: priceTable.source, target: '_blank', rel: 'noopener' }, [priceTable.source]),
    ]),
  );
  return wrap;
}

function persistPrices(): void {
  try {
    const out: Record<string, { input: number; output: number }> = {};
    for (const [k, v] of Object.entries(priceTable.families)) out[k] = { input: v.rates.input, output: v.rates.output };
    localStorage.setItem(PRICES_KEY, JSON.stringify(out));
  } catch {
    /* private mode */
  }
}

const MODALS: Record<string, () => { title: string; body: Node }> = {
  how: () => {
    const frag = document.createDocumentFragment();
    for (const s of HOW_STEPS) {
      frag.append(el('h3', {}, [s.title]));
      frag.append(el('p', {}, [renderCopy(s.body)]));
    }
    return { title: 'How it works', body: frag };
  },
  privacy: () => {
    const frag = document.createDocumentFragment();
    frag.append(el('h3', {}, ['Protected']), list(PRIVACY_PROTECTED));
    frag.append(el('h3', {}, ['Not protected']), list(PRIVACY_NOT));
    frag.append(el('h3', {}, ['Trust surface']), list(PRIVACY_TRUST));
    return { title: 'Privacy & threat model', body: frag };
  },
  prices: () => ({ title: 'Prices', body: priceEditor() }),
  about: () => {
    const tpl = document.getElementById('tpl-about') as HTMLTemplateElement;
    const node = tpl.content.cloneNode(true) as DocumentFragment;
    const body = node.querySelector('#about-body');
    if (body) body.textContent = ABOUT_BODY;
    return { title: 'About runledger', body: node };
  },
};

for (const btn of document.querySelectorAll<HTMLButtonElement>('[data-modal]')) {
  btn.addEventListener('click', () => {
    const key = btn.dataset['modal']!;
    const make = MODALS[key];
    if (!make) return;
    const { title, body } = make();
    modal.show(title, body);
  });
}

/* ── glossary ──────────────────────────────────────────────────────────────────────────────── */

const tooltip = $('tooltip');
document.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  if (!t.classList?.contains('glossary-link')) {
    tooltip.setAttribute('hidden', '');
    return;
  }
  const entry = GLOSSARY[t.dataset['term'] ?? ''];
  if (!entry) return;
  tooltip.replaceChildren(el('strong', {}, [entry.term]), el('span', {}, [entry.def]));
  tooltip.removeAttribute('hidden');
  const r = t.getBoundingClientRect();
  const top = r.bottom + 8;
  tooltip.style.top = `${top}px`;
  tooltip.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - 340))}px`;
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') tooltip.setAttribute('hidden', '');
});

/* ── ingest ────────────────────────────────────────────────────────────────────────────────── */

let picked: File[] = [];

const dropzone = $('dropzone');
const fileStatus = $('file-status');

function setFiles(files: File[]): void {
  // A folder drop brings everything in it; only session logs are of any use.
  const usable = files.filter((f) => f.name.endsWith('.jsonl'));
  const skipped = files.length - usable.length;
  if (!usable.length) {
    showError(
      files.length
        ? `None of those ${files.length} file(s) end in .jsonl. Session logs are one JSON record per line and always have that extension.`
        : 'No files were dropped.',
    );
    return;
  }
  picked = usable;
  const bytes = usable.reduce((n, f) => n + f.size, 0);
  fileStatus.textContent =
    `${formatCount(usable.length)} file${usable.length === 1 ? '' : 's'}, ${formatBytes(bytes)}` +
    (skipped ? ` · ${formatCount(skipped)} non-.jsonl file(s) ignored` : '');
  logEvent(`Loaded ${usable.length} file(s), ${formatBytes(bytes)}.`, 'good');
  run();
}

dropzone.addEventListener('dragover', (e) => {
  e.preventDefault();
  dropzone.classList.add('is-over');
});
dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-over'));
dropzone.addEventListener('drop', async (e) => {
  e.preventDefault();
  dropzone.classList.remove('is-over');
  const dt = e.dataTransfer;
  if (!dt) return;
  const entries = [...dt.items].map((i) => i.webkitGetAsEntry?.()).filter(Boolean) as FileSystemEntry[];
  if (entries.some((en) => en.isDirectory)) {
    const files: File[] = [];
    for (const en of entries) await collect(en, files);
    setFiles(files);
  } else {
    setFiles([...dt.files]);
  }
});
dropzone.addEventListener('click', () => $('files-input').click());
dropzone.addEventListener('keydown', (e) => {
  const k = (e as KeyboardEvent).key;
  if (k === 'Enter' || k === ' ') {
    e.preventDefault();
    $('files-input').click();
  }
});

/** Walk a dropped directory. `readEntries` returns at most 100 at a time and says nothing when it
 *  stops, so the loop is not optional: a single call silently truncates a 3,000-file folder. */
async function collect(entry: FileSystemEntry, out: File[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File | null>((res) =>
      (entry as FileSystemFileEntry).file(res, () => res(null)),
    );
    if (file) out.push(file);
    return;
  }
  if (!entry.isDirectory) return;
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((res) => reader.readEntries(res, () => res([])));
    if (!batch.length) break;
    for (const e of batch) await collect(e, out);
  }
}

$('pick-files').addEventListener('click', (e) => {
  e.stopPropagation();
  $('files-input').click();
});
($('files-input') as HTMLInputElement).addEventListener('change', (e) => {
  const input = e.target as HTMLInputElement;
  setFiles([...(input.files ?? [])]);
  input.value = '';
});

// "Try a sample" feeds the sample through EXACTLY the same path as a real drop — same File, same
// worker, same everything. A separate code path for the demo is how a demo stops proving anything.
$('try-sample').addEventListener('click', async (e) => {
  e.stopPropagation();
  try {
    logEvent('Fetching the bundled sample session.', 'info');
    const res = await fetch('/samples/agent-session.jsonl');
    if (!res.ok) throw new Error(`sample returned ${res.status}`);
    const blob = await res.blob();
    setFiles([new File([blob], 'agent-session.jsonl', { type: 'application/jsonl' })]);
  } catch (err) {
    showError(`Could not load the sample: ${err instanceof Error ? err.message : String(err)}`);
  }
});

/* ── run ───────────────────────────────────────────────────────────────────────────────────── */

function showError(message: string): void {
  $('error-text').textContent = message;
  $('error').removeAttribute('hidden');
  logEvent(message, 'bad');
}

function hideError(): void {
  $('error').setAttribute('hidden', '');
}

$('retry').addEventListener('click', () => {
  hideError();
  if (picked.length) run();
});

function setProgress(p: Progress): void {
  $('progress').removeAttribute('hidden');
  ($('progress-fill') as HTMLElement).style.width = `${Math.round(p.fraction * 100)}%`;
  $('progress-label').textContent = p.label;
}

$('cancel').addEventListener('click', () => {
  worker?.terminate();
  worker = null;
  $('progress').setAttribute('hidden', '');
  logEvent('Cancelled.', 'warn');
});

function rerun(): void {
  if (picked.length) run();
}

function run(): void {
  hideError();
  worker?.terminate();
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

  const files = picked.map((f) => ({ name: f.name, stream: f.stream(), size: f.size }));
  const req: AnalyseRequest = { kind: 'analyse', files, priceTable };

  worker.onmessage = (ev: MessageEvent<WorkerOut>) => {
    const msg = ev.data;
    if (msg.kind === 'progress') setProgress(msg.progress);
    else if (msg.kind === 'error') {
      $('progress').setAttribute('hidden', '');
      showError(msg.message);
    } else if (msg.kind === 'done') {
      $('progress').setAttribute('hidden', '');
      analysis = msg.analysis;
      render(msg.analysis);
    }
  };
  worker.onerror = (e) => {
    $('progress').setAttribute('hidden', '');
    showError(`The analysis worker failed: ${e.message}`);
  };

  setProgress({ phase: 'reading', fraction: 0, label: 'Opening the files' });
  // The streams are transferable, and transferring them is what keeps a 500 MB file from being
  // copied into the worker's heap.
  worker.postMessage(req, files.map((f) => f.stream) as unknown as Transferable[]);
}

/* ── render ────────────────────────────────────────────────────────────────────────────────── */

function statTile(label: string, value: string, note?: string): HTMLElement {
  return el('div', { class: 'stat' }, [
    el('div', { class: 'stat-value' }, [value]),
    el('div', { class: 'stat-label' }, [label]),
    ...(note ? [el('div', { class: 'stat-note' }, [note])] : []),
  ]);
}

function render(a: Analysis): void {
  const headline = headlineFor(a);
  $('verdict-panel').removeAttribute('hidden');
  $('verdict-body').textContent = headline;

  const readShare = a.totalCost > 0 ? a.costByBucket.read / a.totalCost : 0;
  $('stat-row').replaceChildren(
    statTile('total', money(a.totalCost), `${formatCount(a.turnCount)} turns`),
    statTile('carried in from the start', formatTokens(a.baselineTokens), 'tokens, before your first word'),
    statTile('spent re-reading', `${(readShare * 100).toFixed(0)}%`, 'of the bill is cache reads'),
    statTile('context resets', formatCount(a.resetCount), a.resetCount ? 'whole context re-written' : 'none found'),
  );

  // Buckets
  const bt = $('buckets-table') as HTMLTableElement;
  bt.replaceChildren(
    el('thead', {}, [
      el('tr', {}, [
        el('th', {}, ['bucket']),
        el('th', { class: 'n' }, ['tokens']),
        el('th', { class: 'n' }, ['cost']),
        el('th', { class: 'n' }, ['share']),
      ]),
    ]),
    el(
      'tbody',
      {},
      (['read', 'output', 'write1h', 'write5m', 'input'] as const).map((b) =>
        el('tr', {}, [
          el('td', {}, [
            b === 'read' ? renderCopy('[[cache-read|cache read]]') : b === 'input' ? 'input' : b.startsWith('write') ? renderCopy(`[[cache-write|cache write]] (${b === 'write5m' ? '5 min' : '1 hour'})`) : b,
          ]),
          el('td', { class: 'n mono' }, [formatTokens(a.tokensByBucket[b])]),
          el('td', { class: 'n mono' }, [money(a.costByBucket[b])]),
          el('td', { class: 'n mono' }, [a.totalCost > 0 ? `${((a.costByBucket[b] / a.totalCost) * 100).toFixed(1)}%` : '—']),
        ]),
      ),
    ),
  );
  $('buckets-panel').removeAttribute('hidden');
  $('coverage-note').textContent =
    `Priced from the list of ${a.priceTable.asOf}. This decomposition accounts for ` +
    `${(a.readCoverage * 100).toFixed(1)}% of the reported cache-read tokens` +
    (a.unpricedModels.length ? ` · ${a.unpricedModels.length} model(s) had no price and were counted but not priced.` : '.');

  // Ledger
  renderLedger(a);
  $('ledger-panel').removeAttribute('hidden');
  $('ledger-note').replaceChildren(
    renderCopy(
      'Every row is one turn: what it added, and how many later turns re-read it. The token counts ' +
        'are the ones the session reported — nothing here is estimated. Rows are ranked by what ' +
        'they cost in total, [[carry|carry]] plus the write.',
    ),
  );

  // Repeats
  if (a.repeats.length) {
    const rt = $('repeats-table') as HTMLTableElement;
    rt.replaceChildren(
      el('thead', {}, [
        el('tr', {}, [
          el('th', {}, ['block']),
          el('th', { class: 'n' }, ['times']),
          el('th', { class: 'n' }, ['bytes each']),
          el('th', { class: 'n' }, ['repeated bytes']),
        ]),
      ]),
      el(
        'tbody',
        {},
        a.repeats.slice(0, 25).map((r) =>
          el('tr', {}, [
            el('td', {}, [r.label]),
            el('td', { class: 'n mono' }, [formatCount(r.count)]),
            el('td', { class: 'n mono' }, [formatBytes(r.bytes)]),
            el('td', { class: 'n mono' }, [formatBytes(r.repeatBytes)]),
          ]),
        ),
      ),
    );
    $('repeats-note').textContent =
      `${((a.repeatBytes / Math.max(1, a.appendedBytes)) * 100).toFixed(1)}% of appended bytes are byte-identical ` +
      `repeats. Reported in bytes and never in dollars: a turn appends several blocks under one exact ` +
      `token count, and splitting that count between them by size would be a guess.`;
    $('repeats-panel').removeAttribute('hidden');
  } else {
    $('repeats-panel').setAttribute('hidden', '');
  }

  $('export-panel').removeAttribute('hidden');
  $('export-note').textContent =
    'Token counts, tool names and short labels. Neither file contains a line of your session.';
  $('verdict-heading').focus();

  logEvent(
    `Analysed ${formatCount(a.turnCount)} turns across ${a.sessions.length} file(s): ` +
      `${money(a.totalCost)}, coverage ${(a.readCoverage * 100).toFixed(1)}%.`,
    'good',
  );
  for (const s of a.sessions) {
    if (s.badLines) logEvent(`${s.fileName}: ${s.badLines} unreadable line(s) skipped.`, 'warn');
  }
  if (a.resetCount) logEvent(`${a.resetCount} context reset(s) found.`, 'warn');
  if (a.unpricedModels.length) logEvent(`No price for: ${a.unpricedModels.join(', ')}.`, 'warn');
}

function renderLedger(a: Analysis): void {
  const rows = rowLimit > 0 ? a.ledger.slice(0, rowLimit) : a.ledger;
  const t = $('ledger-table') as HTMLTableElement;
  t.replaceChildren(
    el('thead', {}, [
      el('tr', {}, [
        el('th', { class: 'n' }, ['#']),
        el('th', { class: 'n' }, ['turn']),
        el('th', {}, ['what it added']),
        el('th', { class: 'n' }, ['tokens']),
        el('th', { class: 'n' }, ['re-reads']),
        el('th', { class: 'n' }, ['carry']),
        el('th', { class: 'n' }, ['cost']),
      ]),
    ]),
    el(
      'tbody',
      {},
      rows.map((r, i) =>
        el('tr', { class: r.kind === 'baseline' ? 'row-baseline' : '' }, [
          el('td', { class: 'n mono' }, [String(i + 1)]),
          el('td', { class: 'n mono' }, [r.kind === 'baseline' && r.turnIndex === 1 ? '—' : String(r.turnIndex)]),
          el('td', {}, [r.label]),
          el('td', { class: 'n mono' }, [formatTokens(r.tokens)]),
          el('td', { class: 'n mono' }, [formatCount(r.reReads)]),
          el('td', { class: 'n mono' }, [formatTokens(r.carryTokens)]),
          el('td', { class: 'n mono' }, [money(r.carryCost + r.writeCost)]),
        ]),
      ),
    ),
  );
}

($('filter-rows') as HTMLSelectElement).addEventListener('change', (e) => {
  rowLimit = Number((e.target as HTMLSelectElement).value);
  if (analysis) renderLedger(analysis);
});

/* ── artefacts ─────────────────────────────────────────────────────────────────────────────── */

/** One download per explicit click. Chrome prompts on the second in a gesture; Safari drops it. */
function download(name: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = el('a', { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  logEvent(`Wrote ${name}.`, 'good');
}

$('dl-report').addEventListener('click', () => {
  if (!analysis) return;
  download('run-report.html', reportHtml(analysis, headlineFor(analysis)), 'text/html');
});
$('dl-csv').addEventListener('click', () => {
  if (!analysis) return;
  download('carry-ledger.csv', ledgerCsv(analysis), 'text/csv');
});

const shareBtn = $('share') as HTMLButtonElement;
if ('share' in navigator) {
  shareBtn.removeAttribute('hidden');
  shareBtn.addEventListener('click', async () => {
    if (!analysis) return;
    const file = new File([reportHtml(analysis, headlineFor(analysis))], 'run-report.html', { type: 'text/html' });
    try {
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: 'runledger report' });
      else await navigator.share({ title: 'runledger', text: headlineFor(analysis) });
    } catch {
      /* the user dismissed the sheet */
    }
  });
}

$('start-over').addEventListener('click', () => {
  picked = [];
  analysis = null;
  clearLog();
  for (const id of ['verdict-panel', 'buckets-panel', 'ledger-panel', 'repeats-panel', 'export-panel']) {
    $(id).setAttribute('hidden', '');
  }
  fileStatus.textContent = '';
  hideError();
  logEvent('Cleared.', 'info');
});

/* ── folder picker, where it exists ────────────────────────────────────────────────────────── */

if ('showDirectoryPicker' in window) {
  const btn = $('pick-folder') as HTMLButtonElement;
  btn.removeAttribute('hidden');
  btn.addEventListener('click', async (e) => {
    e.stopPropagation();
    try {
      // Read-only, and never persisted.
      const dir = await (window as unknown as {
        showDirectoryPicker: (o: { mode: string }) => Promise<FileSystemDirectoryHandle>;
      }).showDirectoryPicker({ mode: 'read' });
      const files: File[] = [];
      for await (const handle of (dir as unknown as AsyncIterable<FileSystemHandle>)) {
        if (handle.kind === 'file') files.push(await (handle as FileSystemFileHandle).getFile());
      }
      setFiles(files);
    } catch {
      /* the user cancelled the picker */
    }
  });
}

logEvent('Ready. Nothing has been read yet.', 'info');
